/**
 * Cycle de vie du document et remise de fichiers — EF-MOD-008, EF-FIC-002.
 */

export type VisibilityListener = (visible: boolean) => void;

/**
 * Observe le passage à l'état masqué et le retour à l'état visible.
 *
 * Au passage à l'arrière-plan, l'application interrompt les mises à jour de
 * localisation, demande une sauvegarde locale et considère le Wake Lock comme
 * libéré, sans produire aucune activité réseau.
 */
export function observeVisibility(listener: VisibilityListener): () => void {
  if (typeof document === 'undefined') return () => undefined;

  const onVisibilityChange = (): void => {
    listener(document.visibilityState === 'visible');
  };
  // `pagehide` couvre les cas où `visibilitychange` n'est pas émis avant que le
  // contexte web soit détruit.
  const onPageHide = (): void => {
    listener(false);
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  globalThis.addEventListener('pagehide', onPageHide);

  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    globalThis.removeEventListener('pagehide', onPageHide);
  };
}

export interface PreparedExport {
  readonly fileName: string;
  readonly bytes: number;
  /** Empreinte SHA-256 du contenu remis au navigateur. */
  readonly sha256: string;
  readonly preparedAt: string;
}

async function sha256Hex(text: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (subtle === undefined) return '';
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Remet un fichier au mécanisme de téléchargement ou de partage du navigateur.
 *
 * L'application affiche « export préparé » et le nom proposé ; elle n'affirme
 * pas que le fichier a été définitivement enregistré, ce choix appartenant au
 * navigateur et à l'utilisateur.
 */
export async function deliverFile(fileName: string, content: string): Promise<PreparedExport> {
  const blob = new Blob([content], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Laisse au navigateur le temps d'ouvrir la remise avant de révoquer l'URL.
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 10_000);

  return {
    fileName,
    bytes: blob.size,
    sha256: await sha256Hex(content),
    preparedAt: new Date().toISOString(),
  };
}

/** Ouvre le sélecteur de fichiers du navigateur et retourne le fichier choisi. */
export function pickFile(accept: string): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.style.display = 'none';

    let settled = false;
    const finish = (file: File | null): void => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(file);
    };

    input.addEventListener('change', () => {
      finish(input.files?.[0] ?? null);
    });
    // Une annulation ne produit pas d'événement `change` sur tous les navigateurs.
    input.addEventListener('cancel', () => {
      finish(null);
    });

    document.body.appendChild(input);
    input.click();
  });
}

/** Copie un texte dans le presse-papiers — EF-RBK-011. */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
